import { useCallback, useEffect } from 'react'
import { useBrowserLocation } from 'wouter/use-browser-location'
import { useAppCtx } from '../providers/useAppCtx'

type NavigateOptions = {
  replace?: boolean
  state?: unknown
}

type LocationHook = () => [
  string,
  (to: string, options?: NavigateOptions) => void,
]

export function createConfirmingLocationHook(
  message: string,
  paths: string[],
): LocationHook {
  const pathsSet = new Set(paths)

  const useConfirmingLocation: LocationHook = () => {
    const [location, baseNavigate] = useBrowserLocation()
    const { onEvent } = useAppCtx()

    const isInGuarded = pathsSet.has('/' + location.split('/').at(-1)!)

    useEffect(() => {
      if (!isInGuarded) {
        return
      }

      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault()
        e.returnValue = message
        return message
      }

      window.addEventListener('beforeunload', handleBeforeUnload)

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload)
      }
    }, [isInGuarded])

    const navigate = useCallback(
      (to: string, options?: NavigateOptions) => {
        const isChangingRoute = to !== location

        if (isInGuarded && isChangingRoute && !options?.replace) {
          const shouldLeave = window.confirm(message)

          if (!shouldLeave) {
            return
          }
        }

        baseNavigate(to, options as any)

        onEvent('page-load', {
          path: to,
          hostname: window.location.hostname,
        })
      },
      [location, baseNavigate, isInGuarded, onEvent],
    )

    return [location, navigate]
  }

  return useConfirmingLocation
}
